import { useEffect, useState, type ChangeEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/home/buttons';
import { Card } from 'src/components/home/card';
import { Input } from 'src/components/home/input';
import { SpinTextLoader } from 'src/components/loader/spin-loader';
import { getJobState, requestStripeCheckout } from 'src/service/api.service';
import { formatCurrency } from 'src/util/currency';
import { JobState, type GetJobLambdaResultDto } from 'unvox-common';

type PaymentState = 'error' | 'validatingJobId' | 'ready' | 'stripeLoading';

function isValidEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isLoadingState(state: PaymentState) {
    return ['validatingJobId', 'stripeLoading'].includes(state);
}

export function PaymentRoute(props: { showCheckoutCancelledNotice?: boolean }) {
    const params = useParams();
    const jobId = (params.jobId || '').trim();

    if (!jobId) {
        return <Navigate to="/" replace />;
    }

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [paymentState, setPaymentState] = useState<PaymentState>('validatingJobId');
    const [jobData, setJobData] = useState<GetJobLambdaResultDto>();
    const [email, setEmail] = useState('');
    const [formError, setFormError] = useState('');

    const { showCheckoutCancelledNotice } = props;
    const checkoutPrice = formatCurrency(jobData?.stripe?.amountTotal, jobData?.stripe?.currency);
    const checkoutActionText = checkoutPrice
        ? t('route.payment.txtCheckoutAction', { price: checkoutPrice })
        : t('route.payment.txtCheckoutActionFallback');

    useEffect(() => {
        let isMounted = true;
        let timeoutId: number | undefined;

        async function getJobData() {
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
                timeoutId = undefined;
            }

            const result = await getJobState(jobId);

            if (!isMounted) {
                return;
            }

            if (result.error) {
                if (result.error.code === 'no_such_job') {
                    navigate('/invalid-job', { replace: true });
                } else {
                    setPaymentState('error');
                }

                return;
            }

            if (!result.data) {
                return setPaymentState('error');
            }

            if (result.data.state === JobState.AwaitingProcessingTask || result.data.state === JobState.Completed || result.data.state === JobState.Failed) {
                return navigate(`/processing?jobId=${encodeURIComponent(jobId)}`, { replace: true });
            }

            if (!result.data.previewUrl) {
                return navigate(`/preview/${encodeURIComponent(jobId)}`, { replace: true });
            }

            setJobData(result.data);
            setPaymentState('ready');

            if (result.data.state === JobState.AwaitingPayment && result.data.stripe?.sessionId) {
                timeoutId = window.setTimeout(() => void getJobData(), 3000);
            }
        }

        void getJobData();

        return () => {
            isMounted = false;
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [jobId, navigate]);

    function onInputChanged(event: ChangeEvent<HTMLInputElement>) {
        setEmail(event.target.value.trim());
    }

    async function handleCheckout() {
        if (!isValidEmail(email)) {
            return setFormError(t('route.payment.txtInvalidEmail'));
        }

        setFormError('');
        setPaymentState('stripeLoading');

        const response = await requestStripeCheckout({ email, jobId });

        if (response.error) {
            if (response.error.code === 'no_such_job') {
                return navigate('/invalid-job', { replace: true });
            }

            setPaymentState('ready');
            return setFormError(response.error.message || t('route.payment.txtCheckoutFailed'));
        }

        if (!response.data) {
            setPaymentState('ready');
            return setFormError(t('route.payment.txtCheckoutFailed'));
        }

        window.location.assign(response.data.checkoutUrl);
    }

    if (paymentState === 'error') {
        return (
            <Card>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-foreground">{t('route.payment.txtUnavailableTitle')}</h2>
                        <p className="text-sm text-secondary">{t('route.payment.txtUnavailableBody')}</p>
                    </div>
                    <Button variant="secondary" onClick={() => window.location.reload()} className="w-full">
                        {t('global.action.txtRetry')}
                    </Button>
                    <Button variant="primary" onClick={() => navigate('/')} className="w-full">
                        {t('global.action.txtStartOver')}
                    </Button>
                </div>
            </Card>
        );
    }

    if (paymentState === 'validatingJobId') {
        return (
            <Card>
                <div className="flex flex-col items-center gap-2 text-center">
                    <h2 className="text-2xl font-semibold text-foreground">{t('route.payment.txtPreparingTitle')}</h2>
                    <SpinTextLoader text={t('global.job.txtCheckingStatus')} />

                    <div className="mt-2 text-xs text-secondary">
                        {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <div className="flex flex-col items-center gap-3 text-center">
                <div className="w-full space-y-4 text-left">
                    {!!showCheckoutCancelledNotice && (
                        <p className="rounded-xl border border-green-200 bg-green-100/70 px-4 py-3 text-xs shadow-[0_12px_24px_-22px_rgba(40,30,80,0.35)]">
                            {t('route.payment.txtCheckoutCancelledBody')}
                        </p>
                    )}
                    {jobData?.previewUrl && (
                        <>
                            <div className="space-y-2">
                                <label className="text-xs uppercase tracking-[0.25em] text-secondary">{t('route.payment.txtTrackPreviewLabel')}</label>
                                <div className="rounded-xl bg-gray-100">
                                    <audio controls src={jobData.previewUrl} className="w-full" />
                                </div>
                                <p className="text-xs text-secondary">{t('route.payment.txtTrackPreviewHelper')}</p>
                            </div>
                            <hr className="my-8 text-muted" />
                        </>
                    )}
                    <div className="space-y-2">
                        <label className="text-xs uppercase tracking-[0.25em] text-secondary">{t('route.payment.txtEmailLabel')}</label>
                        <Input type="email" placeholder={t('route.payment.txtEmailPlaceholder')} value={email} disabled={isLoadingState(paymentState)} onChange={onInputChanged} />
                        <p className="text-xs text-secondary">{t('route.payment.txtEmailCheckoutHelper')}</p>
                    </div>
                </div>
                {!!formError && <p className="text-sm text-danger">{formError}</p>}
                <Button onClick={handleCheckout} isLoading={isLoadingState(paymentState)} disabled={isLoadingState(paymentState)} className="w-full">
                    {checkoutActionText}
                </Button>
                <div className="text-xs text-secondary">
                    {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                </div>
            </div>
        </Card>
    );
}
